import { LINKS } from "@/constants/links.constant";

interface KeyType {
  title: string;
  description: string;
  url: string;
  image: string;
}

/**
 * A constant array of key applications (`KEY_APPS`) within the Blessnet ecosystem.
 * Each application provides unique functionality and features, represented as objects
 * with the following properties:
 *
 * - `title`: The name of the application.
 * - `description`: A brief explanation of the application's purpose and functionality.
 * - `url`: A link to the application's detailed page or resource.
 * - `anchors`: An array of anchor links related to the application for navigation.
 * - `image`: A reference to an image asset representing the application.
 *
 * Example applications include:
 * - Modular permissions for authorizing new addresses.
 * - Ethereals for mirroring assets from other blockchains.
 * - Mirrors for accessing real-time data across chains.
 * - Attestations for generating on-chain proofs.
 * - Transaction Pool for cross-chain transaction settlement.
 * - Bless Token for multi-chain governance and bridging.
 *
 * This constant is typed as an array of `KeyType` objects.
 */
export const KEY_APPS: KeyType[] = [
  {
    title: "Modular permissions",
    description:
      "Authorising a new address on Blessnet is as simple as plugging in a new game controller.",
    url: LINKS.KEY_APP.MODULAR_PERMISSIONS,
    image: require("@public/web-content/bless-controller.png"),
  },
  {
    title: "Ethereals",
    description:
      "Your assets from other blockchains are seamlessly mirrored and accessible within the Blessnet ecosystem.",
    url: LINKS.KEY_APP.ETHEREALS,
    image: require("@public/web-content/bless-patch.png"),
  },
  {
    title: "Mirrors",
    description:
      "Blessnet mirrors real-time data from other chains, letting users access familiar features across multiple protocols and chains.",
    url: LINKS.KEY_APP.MIRRORS,
    image: require("@public/web-content/bless-reflect-blessed.png"),
  },
  {
    title: "Attestations",
    description:
      "Generate an on-chain proof for anything, without using your wallets on high-value chains.",
    url: LINKS.KEY_APP.ATTESTATIONS,
    image: require("@public/web-content/bless-wink.png"),
  },
  {
    title: "Transaction Pool",
    description:
      "Transactions on Blessnet are purpose built to go somewhere. Use the Transaction Pool for actions that settle on other chains.",
    url: LINKS.KEY_APP.TRANSACTION_POOL,
    image: require("@public/web-content/bless-box.png"),
  },
  {
    title: "Bless Token",
    description:
      "BLESS is multi-chain, multi-gov ready and can self-bridge to a single consistent contract address. It’s one flexible beast!",
    url: LINKS.KEY_APP.BLESS_TOKEN,
    image: require("@public/web-content/bless-portal.png"),
  },
];
